<template>
  <div class="homeview_container">
    <div class="top_control_bar">
      <div class="start_controls">
        <el-button
            type="success"
            @click="startCopilot" v-show="state==='end'" :loading="copilot_starting"
            :disabled="copilot_starting">
            <i class="el-icon-microphone"></i> Start Copilot
        </el-button>
        <el-button
            :loading="copilot_stopping"
            @click="userStopCopilot" v-show="state==='ing'">
            <i class="el-icon-video-pause"></i> Stop Copilot
        </el-button>
      </div>
      <MyTimer ref="MyTimer" style="margin-left: 10px;"/>
      <div class="settings_controls">
        <el-switch
          v-model="autoAskEnabled"
          active-text="自动问答"
          inactive-text="手动问答"
          style="margin-right: 20px;"
        />
        <el-switch
          v-model="showMarkdown"
          active-text="Markdown"
          inactive-text="Plain"
          style="margin-left: auto;"
        />
      </div>
    </div>

    <div class="main_content">
      <div class="transcript_panel">
        <div class="panel_header">
          <i class="el-icon-microphone"></i>
          <span>Speech Recognition Results</span>
          <el-button icon="el-icon-delete" type="text" size="mini" :disabled="!currentText" @click="clearASRContent">
            Clear Text
          </el-button>
        </div>
        <div class="panel_content">
          <div v-if="!currentText" class="empty_state">No speech content yet. Start Copilot to begin recording.</div>
          <div class="asr_content" v-else>{{ currentText }}</div>
        </div>
        <div class="panel_footer">
          <div v-if="autoAskEnabled && questionDetected" class="auto_ask_indicator">
            <i class="el-icon-loading"></i> 检测到问题，准备自动提问...
          </div>
          <el-button type="primary" icon="el-icon-thumb" @click="askCurrentText" :disabled="!isGetGPTAnswerAvailable">
            Ask GPT
          </el-button>
        </div>
      </div>

      <div class="answer_panel">
        <div class="panel_header">
          <i class="el-icon-s-custom"></i>
          <span>GPT Answer</span>
          <el-button type="text" size="mini" icon="el-icon-time" @click="toggleHistoryView" v-if="answerHistory.length > 0">
            {{ showHistory ? '隐藏历史' : '显示历史' }} ({{ answerHistory.length }})
          </el-button>
        </div>
        
        <!-- 历史回答侧边栏 -->
        <div class="history_sidebar" v-if="showHistory && answerHistory.length > 0">
          <div class="history_header">
            <span>历史问答</span>
            <el-button type="text" size="mini" icon="el-icon-delete" @click="clearAllHistory">清空</el-button>
          </div>
          <div class="history_list">
            <div v-for="(item, index) in answerHistory" :key="index" 
                class="history_item" 
                :class="{'active': selectedHistoryIndex === index}"
                @click="selectHistoryItem(index)">
              <div class="history_item_title">{{ truncateText(item.title || '无标题', 60) }}</div>
              <div class="history_item_time">{{ item.timestamp }}</div>
            </div>
          </div>
        </div>
        
        <div class="panel_content" :class="{'with-history': showHistory && answerHistory.length > 0}">
          <!-- 历史回答内容（如果选择了历史项目） -->
          <div v-if="selectedHistoryIndex !== null" class="history_content">
            <div class="history_content_header">
              <el-button type="text" icon="el-icon-back" @click="backToCurrentAnswer">返回当前回答</el-button>
              <span class="history_question">{{ answerHistory[selectedHistoryIndex].title }}</span>
            </div>
            <div class="history_content_body" v-html="formatHistoryAnswer(answerHistory[selectedHistoryIndex].answer)"></div>
          </div>
          
          <!-- 当前回答区域 -->
          <div v-else>
            <LoadingIcon v-show="show_ai_thinking_effect"/>
            <div v-if="!ai_result && !show_ai_thinking_effect" class="empty_state">AI回答将在这里显示</div>
            <div v-else class="ai_result_content" v-html="formattedAiResult" :class="{'typing-effect': showTypingEffect}"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Assert from "assert-js"
import LoadingIcon from "@/components/LoadingIcon.vue";
import MyTimer from "@/components/MyTimer.vue";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import OpenAI from "openai";
import config_util from "../utils/config_util"
import { marked } from 'marked';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import 'highlight.js/styles/github-dark.css';

// 提取Markdown渲染逻辑到独立工具函数
const markdownUtils = {
  // 缓存渲染结果，提高性能
  cache: new Map(),
  
  // 初始化marked配置
  init() {
    // 配置 marked 选项
    marked.setOptions({
      highlight: function(code, lang) {
        if (lang && hljs.getLanguage(lang)) {
          return hljs.highlight(code, { language: lang }).value;
        }
        return hljs.highlightAuto(code).value;
      },
      breaks: true,
      gfm: true,
      sanitize: false,
      smartLists: true,
      smartypants: true
    });
    
    return this;
  },
  
  // 渲染Markdown内容
  render(text, useCache = true) {
    if (!text) return '';
    
    // 使用缓存以提高性能
    if (useCache && this.cache.has(text)) {
      return this.cache.get(text);
    }
    
    const result = marked(text);
    
    // 存入缓存
    if (useCache) {
      this.cache.set(text, result);
      
      // 限制缓存大小
      if (this.cache.size > 100) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }
    }
    
    return result;
  },
  
  // 仅转换换行为<br>的简单渲染
  renderPlainText(text) {
    if (!text) return '';
    return text.replace(/\n/g, '<br>');
  }
}.init();

export default {
  name: 'HomeView',
  props: {},
  computed: {
    isDevMode() {
      return (process.env.NODE_ENV === 'development')
    },
    isGetGPTAnswerAvailable() {
      // return this.state === "ing" && !!this.currentText
      return !!this.currentText
    },
    formattedAiResult() {
      if (!this.ai_result) return '';
      
      // 如果不显示 Markdown，直接返回纯文本
      if (!this.showMarkdown) {
        return markdownUtils.renderPlainText(this.ai_result);
      }
      
      // 使用提取的工具函数渲染Markdown
      return markdownUtils.render(this.ai_result);
    }
  },
  components: {LoadingIcon, MyTimer},
  data() {
    return {
      currentText: "",
      state: "end", //end\ing
      ai_result: null,
      copilot_starting: false, //显示loading
      copilot_stopping: false,
      show_ai_thinking_effect: false,
      popStyle: {},
      showTypingEffect: false,
      showMarkdown: true, // 添加 Markdown 显示开关
      answerHistory: [], // 存储历史问答记录
      showHistory: false, // 控制是否显示历史面板
      selectedHistoryIndex: null, // 当前选中的历史记录索引
      autoAskEnabled: true, // 自动问答开关
      debounceTimer: null, // 防抖定时器
      lastProcessedText: "", // 上次处理过的文本
      questionDetected: false, // 是否检测到问题
    }
  },
  async mounted() {
    console.log("mounted")
    if (this.isDevMode) {
      // this.currentText = demo_text
    }
  },
  beforeDestroy() {
  },
  methods: {
    async askCurrentText() {
      const apiKey = localStorage.getItem("openai_key")
      
      // 优化：避免重复计算，提前存储状态
      const currentQuestion = this.currentText
      const hasExistingResult = !!this.ai_result
      
      // 避免在空文本时发送请求
      if (!currentQuestion.trim()) {
        this.$message.warning('请先输入或识别一些内容');
        return;
      }
      
      // 重置状态
      this.ai_result = ""
      this.show_ai_thinking_effect = true
      this.showTypingEffect = false
      this.questionDetected = false
      
      // 配置参数提取
      const model = config_util.gpt_model()
      const gpt_system_prompt = config_util.gpt_system_prompt()
      const apiProvider = config_util.api_provider()
      const apiBaseUrl = config_util.api_base_url()
      
      // 优化transcript长度
      const optimizedContent = this.optimizeTranscriptLength(currentQuestion)

      try {
        if (!apiKey) {
          throw new Error("请设置API密钥")
        }

        if (!apiBaseUrl) {
          throw new Error("请设置API基础URL")
        }

        // 根据不同提供商配置OpenAI客户端
        const openaiConfig = {
          apiKey: apiKey,
          dangerouslyAllowBrowser: true
        };
        
        // 根据提供商设置不同的baseURL和headers
        if (apiProvider === 'openrouter') {
          openaiConfig.baseURL = apiBaseUrl;
          openaiConfig.defaultHeaders = {
            "HTTP-Referer": window.location.origin,
            "X-Title": "Interview-Copilot",
          };
        } else if (apiProvider === 'openai') {
          openaiConfig.baseURL = apiBaseUrl;
        } else if (apiProvider === 'azure') {
          // Azure OpenAI需要特殊处理
          openaiConfig.baseURL = apiBaseUrl;
          openaiConfig.defaultQuery = { "api-version": "2023-05-15" };
          // Azure使用api-key作为header而不是Authorization: Bearer
          openaiConfig.defaultHeaders = {
            "api-key": apiKey
          };
        } else {
          // 自定义兼容服务
          openaiConfig.baseURL = apiBaseUrl;
        }

        const openai = new OpenAI(openaiConfig);

        // 创建带流式响应的聊天完成
        const stream = await openai.chat.completions.create({
          model: model,
          messages: [
            { role: "system", content: gpt_system_prompt },
            { role: "user", content: optimizedContent }
          ],
          stream: true,
        });
        
        this.show_ai_thinking_effect = false;
        this.showTypingEffect = true;
        
        // 性能优化：使用累加器减少状态更新次数
        let accumulatedText = '';
        const updateInterval = 50; // 毫秒
        let lastUpdateTime = Date.now();
        
        // 处理流式响应
        for await (const chunk of stream) {
          const text = chunk.choices[0]?.delta?.content || "";
          accumulatedText += text;
          
          // 限制更新频率，避免过多渲染
          const currentTime = Date.now();
          if (currentTime - lastUpdateTime > updateInterval) {
            this.ai_result += accumulatedText;
            accumulatedText = '';
            lastUpdateTime = currentTime;
          }
        }
        
        // 确保最后的文本被添加
        if (accumulatedText) {
          this.ai_result += accumulatedText;
        }
      } catch (e) {
        this.show_ai_thinking_effect = false;
        console.error("API请求错误:", e);
        this.ai_result = `发生错误: ${e.message || e}`;
        
        // 向用户显示友好错误
        this.$message.error(`请求失败: ${e.message || '未知错误'}`);
      } finally {
        this.showTypingEffect = false;
        
        // 添加到历史记录
        if (this.ai_result) {
          const titleSummary = this.extractSummaryFromAnswer(this.ai_result);
          
          // 添加新回答到历史记录
          this.answerHistory.push({
            question: currentQuestion,
            answer: this.ai_result,
            title: titleSummary,
            timestamp: new Date().toLocaleString()
          });
          
          // 限制历史记录数量，避免内存过大
          if (this.answerHistory.length > 50) {
            this.answerHistory = this.answerHistory.slice(-50);
          }
        }
      }
    },
    // 优化transcript长度的方法
    optimizeTranscriptLength(text) {
      if (!text) return "";
      
      // 以行为单位分割文本
      const lines = text.split('\n').filter(line => line.trim().length > 0);
      
      // 如果文本太短，直接返回
      const MIN_LINES = 2;
      if (lines.length <= MIN_LINES) return text;
      
      // 如果文本太长，进行优化
      const MAX_TOKENS = 4000; // 大致估计的最大token数
      const MAX_LINES = 50; // 最大行数限制
      
      // 如果行数超过限制，保留最后的对话内容
      if (lines.length > MAX_LINES) {
        // 先尝试找到面试官的最后几个问题
        const interviewerLines = [];
        const candidateLines = [];
        
        // 从后向前扫描，找出面试官和候选人的对话
        for (let i = lines.length - 1; i >= 0; i--) {
          const line = lines[i].trim();
          // 假设面试官的问题通常以问号结尾，或包含特定关键词
          if (line.endsWith('?') || 
              /please|could you|tell me|explain|describe|implement|solve|write|code/i.test(line)) {
            interviewerLines.unshift(lines[i]);
          } else {
            candidateLines.unshift(lines[i]);
          }
          
          // 找到足够的面试官问题和候选人回答后停止
          if (interviewerLines.length >= 3 && candidateLines.length >= 3) break;
        }
        
        // 确保至少保留最后的对话
        const lastExchangeIndex = Math.max(0, lines.length - 15);
        const importantContext = lines.slice(0, 5); // 保留开头的几行作为上下文
        const lastExchange = lines.slice(lastExchangeIndex);
        
        // 组合结果
        if (interviewerLines.length > 0 && candidateLines.length > 0) {
          // 如果识别出了面试官和候选人的对话，优先使用这种结构
          return [...importantContext, "...", ...interviewerLines, ...candidateLines].join('\n');
        } else {
          // 否则简单截取开头和结尾
          return [...importantContext, "...", ...lastExchange].join('\n');
        }
      }
      
      // 如果文本长度适中，直接返回
      return text;
    },
    clearASRContent() {
      this.currentText = ""
    },
    clearAllHistory() {
      if (this.answerHistory.length > 0) {
        this.answerHistory = [];
        this.selectedHistoryIndex = null;
        
        // 清除Markdown渲染缓存
        markdownUtils.cache.clear();
      }
    },
    async startCopilot() {
      this.copilot_starting = true
      try {
        // 初始化语音识别
        await this.initSpeechRecognition();
        
        // 开始持续识别
        await this.startContinuousRecognition();
        
        // 启动成功，更新UI状态
        this.copilot_starting = false
        this.state = "ing"
        this.$refs.MyTimer.start()
        // 重置上次处理的文本
        this.lastProcessedText = "";
        console.log("语音识别启动成功");
      } catch (error) {
        this.copilot_starting = false
        this.currentText = typeof error === 'string' ? error : `启动失败: ${error.message || '未知错误'}`
        console.error("语音识别启动失败:", error);
      }
    },
    
    // 初始化语音识别
    async initSpeechRecognition() {
      const token = localStorage.getItem("azure_token")
      const region = config_util.azure_region()
      const language = config_util.azure_language()
      const openai_key = localStorage.getItem("openai_key")
      
      // 检查必要配置
      if (!openai_key) {
        throw new Error("请设置OpenRouter API Token")
      }
      if (!token) {
        throw new Error("请设置Azure语音服务密钥")
      }
      if (!region) {
        throw new Error("请设置Azure区域")
      }

      // 创建语音配置
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(token, region);
      speechConfig.speechRecognitionLanguage = language;
      
      // 创建音频配置
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      
      // 创建识别器
      this.recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      
      // 配置识别事件处理
      this.setupRecognitionEvents();
    },
    
    // 配置语音识别事件
    setupRecognitionEvents() {
      if (!this.recognizer) return;
      
      const sdk = SpeechSDK;
      
      // 识别结果处理
      this.recognizer.recognized = (sender, event) => {
        if (sdk.ResultReason.RecognizedSpeech === event.result.reason && event.result.text.trim().length > 0) {
          const text = event.result.text;
          
          // 添加到当前文本，确保有换行
          const needsNewline = this.currentText && !this.currentText.endsWith('\n');
          this.currentText += (needsNewline ? "\n" : "") + text;
          
          // 触发自动问答检测
          this.debouncedAskGPT();
        } else if (sdk.ResultReason.NoMatch === event.result.reason) {
          console.log("未能识别语音");
        }
      };
      
      // 添加错误处理
      this.recognizer.canceled = (sender, event) => {
        if (event.reason === sdk.CancellationReason.Error) {
          console.error(`语音识别错误: ${event.errorCode} - ${event.errorDetails}`);
          
          // 如果是麦克风或权限问题，提示用户
          if (event.errorCode === sdk.CancellationErrorCode.AuthenticationFailure ||
              event.errorCode === sdk.CancellationErrorCode.ConnectionFailure) {
            this.currentText += "\n[语音识别错误: 请检查Azure认证或网络连接]";
          } else if (event.errorCode === sdk.CancellationErrorCode.MicrophoneUnavailable) {
            this.currentText += "\n[语音识别错误: 无法访问麦克风，请检查麦克风权限]";
          }
          
          // 如果错误严重，停止识别
          if (this.state === "ing") {
            this.userStopCopilot();
          }
        }
      };
    },
    
    // 开始持续识别
    startContinuousRecognition() {
      if (!this.recognizer) {
        throw new Error("语音识别器未初始化");
      }
      
      return new Promise((resolve, reject) => {
        this.recognizer.startContinuousRecognitionAsync(
          () => resolve(),
          (err) => reject(err)
        );
      });
    },
    
    // 停止持续识别
    stopContinuousRecognition() {
      if (!this.recognizer) {
        return Promise.resolve();
      }
      
      return new Promise((resolve, reject) => {
        this.recognizer.stopContinuousRecognitionAsync(
          () => resolve(),
          (err) => reject(err)
        );
      });
    },
    
    async userStopCopilot() {
      if (this.copilot_stopping || this.state !== "ing") return;
      
      this.copilot_stopping = true
      try {
        await this.stopContinuousRecognition();
        console.log("语音识别已停止");
        
        // 更新状态
        this.state = "end"
        this.$refs.MyTimer.stop()
      } catch (error) {
        console.error("停止识别出错:", error);
      } finally {
        this.copilot_stopping = false
      }
    },
    copyCode(event) {
      const codeBlock = event.target.closest('.code-block');
      const codeContent = codeBlock.querySelector('.code-content').textContent;
      
      navigator.clipboard.writeText(codeContent).then(() => {
        const copyBtn = event.target;
        const originalText = copyBtn.textContent;
        copyBtn.textContent = '已复制!';
        copyBtn.classList.add('copied');
        
        setTimeout(() => {
          copyBtn.textContent = originalText;
          copyBtn.classList.remove('copied');
        }, 2000);
      }).catch(err => {
        console.error('无法复制文本: ', err);
      });
    },
    escapeHtml(text) {
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    
    processMarkdownTables(text) {
      // 匹配整个表格结构
      return text.replace(/(\n|^)\|(.*)\|(\n\|(.*)\|)+/g, (match) => {
        const lines = match.trim().split('\n');
        
        if (lines.length < 2) return match; // 至少需要有表头和分隔行
        
        const headerRow = lines[0];
        const delimiterRow = lines[1];
        
        // 验证是否存在分隔行 (由 ----- 组成)
        if (!delimiterRow.match(/\|(\s*:?-+:?\s*\|)+/)) return match;
        
        let tableHtml = '<div class="gpt-table-container"><table class="gpt-table">';
        
        // 处理表头
        const headers = headerRow.split('|').slice(1, -1);
        tableHtml += '<thead><tr>';
        headers.forEach(header => {
          tableHtml += `<th>${header.trim()}</th>`;
        });
        tableHtml += '</tr></thead>';
        
        // 处理表格内容
        if (lines.length > 2) {
          tableHtml += '<tbody>';
          for (let i = 2; i < lines.length; i++) {
            const cells = lines[i].split('|').slice(1, -1);
            if (cells.length === headers.length) {
              tableHtml += '<tr>';
              cells.forEach(cell => {
                tableHtml += `<td>${cell.trim()}</td>`;
              });
              tableHtml += '</tr>';
            }
          }
          tableHtml += '</tbody>';
        }
        
        tableHtml += '</table></div>';
        return tableHtml;
      });
    },
    toggleHistoryView() {
      this.showHistory = !this.showHistory;
      if (!this.showHistory) {
        this.selectedHistoryIndex = null;
      }
    },
    selectHistoryItem(index) {
      this.selectedHistoryIndex = index;
    },
    backToCurrentAnswer() {
      this.selectedHistoryIndex = null;
    },
    truncateText(text, maxLength) {
      if (text.length > maxLength) {
        return text.slice(0, maxLength) + '...';
      }
      return text;
    },
    formatHistoryAnswer(answer) {
      if (!answer) return '';
      
      // 使用抽取的工具函数
      if (!this.showMarkdown) {
        return markdownUtils.renderPlainText(answer);
      }
      
      return markdownUtils.render(answer);
    },
    // 从AI回答中提取摘要作为标题 - 优化方法
    extractSummaryFromAnswer(answer) {
      if (!answer) return '无回答';
      
      // 正则表达式简化，更有效率地去除markdown标记
      const plainText = answer
        .replace(/#{1,6}\s+|[*`]|```[\s\S]*?```|\[.*?\]\(.*?\)/g, '')
        .replace(/\n/g, ' ')
        .trim();
      
      // 使用更精确的句子分割正则表达式
      const sentences = plainText.split(/[.!?。！？]+\s*/);
      const firstSentence = sentences[0];
      
      if (firstSentence && firstSentence.length <= 50) {
        return firstSentence;
      }
      
      return plainText.length > 50 ? plainText.slice(0, 50) + '...' : plainText;
    },
    
    // 检测文本中是否包含问题
    detectQuestion(text) {
      if (!text || text === this.lastProcessedText) return false;
      
      // 获取新增的文本部分
      const newText = text.replace(this.lastProcessedText, '').trim();
      if (!newText) return false;
      
      // 按行分割文本
      const lines = text.split('\n').filter(line => line.trim().length > 0);
      if (lines.length === 0) return false;
      
      // 只检查最后几行的内容（最后3行）
      const lastLines = lines.slice(-3).join(' ');
      
      // 获取新增内容或最后一部分内容进行分析
      const textToAnalyze = newText.length > 0 ? 
        newText : (lastLines.length > 300 ? lastLines.slice(-300) : lastLines);
      
      if (!textToAnalyze.trim()) return false;
      
      // 用正则表达式更精确地匹配问题模式
      const patterns = {
        // 英文问题模式
        enQuestionPattern: /(?:\?|what|how|why|when|where|which|who|could you|can you|please)\s.{3,}(?:\?)?/i,
        
        // 中文问题模式
        cnQuestionPattern: /(?:吗|呢|？|请问|能否|如何|为什么|什么|怎么|谁|哪些|多少|几个|解释|说明).{2,}(?:？)?/i,
        
        // 命令请求模式
        commandPattern: /(?:explain|describe|tell me|show me|implement|write|please|请|帮我|写一个|实现|告诉我).{3,}/i,
        
        // 结尾问号检测
        questionMark: /[?？]\s*$/
      };
      
      // 检查文本长度和最近一段内容是否匹配问题模式
      const isShortQuestion = textToAnalyze.length < 100 && 
        (patterns.enQuestionPattern.test(textToAnalyze) || 
         patterns.cnQuestionPattern.test(textToAnalyze));
      
      // 检查是否以问号结尾
      const endsWithQuestion = patterns.questionMark.test(textToAnalyze);
      
      // 检查是否是命令句式且较短
      const isCommand = textToAnalyze.length < 150 && patterns.commandPattern.test(textToAnalyze);
      
      // 综合判断
      const isQuestion = isShortQuestion || endsWithQuestion || isCommand;
      
      if (isQuestion) {
        console.log("检测到问题:", textToAnalyze);
      }
      
      return isQuestion;
    },
    
    // 处理自动问答的防抖函数
    debouncedAskGPT() {
      if (!this.autoAskEnabled || !this.currentText || this.show_ai_thinking_effect) return;
      
      // 清除之前的定时器
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      
      // 检测文本变化量，如果变化太小，不触发检测
      const textLengthDiff = this.currentText.length - (this.lastProcessedText?.length || 0);
      if (textLengthDiff < 5 && this.lastProcessedText) return;
      
      // 检测是否有问题
      const hasQuestion = this.detectQuestion(this.currentText);
      
      // 更新问题检测状态
      this.questionDetected = hasQuestion;
      
      // 如果检测到问题，设置较长的延迟以确认用户完成提问
      const delay = hasQuestion ? 3000 : 2000;
      
      // 设置新的定时器
      this.debounceTimer = setTimeout(async () => {
        if (this.questionDetected && this.currentText !== this.lastProcessedText) {
          console.log("Auto asking GPT...");
          
          // 再次检查状态，避免重复提问
          if (this.show_ai_thinking_effect) return;
          
          await this.askCurrentText(); // 等待askCurrentText完成
          this.questionDetected = false; // 重置问题检测状态
          this.lastProcessedText = this.currentText; // 更新处理过的文本
        }
      }, delay);
    }
  }
}


const demo_text = `
Hello, thank you for coming for the interview. Please introduce yourself.

I'm Jhon, currently an undergraduate student majoring in Data Science at HK University. I am in the top 10% of my class, specializing in deep learning and proficient in web development. Additionally, I have contributed to several well-known open-source projects as mentioned in my resume.

Alright, let me ask you a machine learning question.

Sure, go ahead.

Can you explain the Hidden Markov Model?
`

async function sleep(ms) {
  return new Promise((resolve => setTimeout(resolve, ms)))
}


</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.homeview_container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f7fa;
  overflow: hidden;
}

.top_control_bar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.start_controls {
  display: flex;
  gap: 10px;
}

.settings_controls {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.main_content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 15px;
  gap: 15px;
  overflow: hidden;
}

.transcript_panel, .answer_panel {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.transcript_panel {
  flex: 1;
  min-height: 180px;
  max-height: 30vh;
}

.answer_panel {
  flex: 2;
  position: relative;
  display: flex;
  flex-direction: column;
}

.panel_header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eaecef;
  background-color: #f6f8fa;
}

.panel_header i {
  margin-right: 8px;
  color: #42b983;
}

.panel_header span {
  flex-grow: 1;
  font-weight: 500;
  color: #2c3e50;
}

.panel_content {
  flex-grow: 1;
  overflow-y: auto;
  padding: 16px;
  position: relative;
}

.panel_content.with-history {
  margin-left: 250px;
}

.panel_footer {
  padding: 12px 16px;
  border-top: 1px solid #eaecef;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.auto_ask_indicator {
  color: #42b983;
  margin-right: auto;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.auto_ask_indicator i {
  margin-right: 5px;
  font-size: 16px;
}

.asr_content, .ai_result_content {
  white-space: pre-wrap;
  line-height: 1.5;
}

.ai_result_content {
  padding: 8px 0;
}

.empty_state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #909399;
  font-style: italic;
}

.history_sidebar {
  position: absolute;
  top: 55px; /* 49px是panel_header的高度 */
  left: 0;
  width: 250px;
  height: calc(100% - 55px);
  border-right: 1px solid #eaecef;
  background-color: white;
  display: flex;
  flex-direction: column;
  z-index: 5;
}

.history_header {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eaecef;
  font-weight: 500;
  color: #42b983;
}

.history_list {
  flex-grow: 1;
  overflow-y: auto;
}

.history_item {
  padding: 10px 15px;
  border-bottom: 1px solid #eaecef;
  cursor: pointer;
  transition: background-color 0.2s;
}

.history_item:hover {
  background-color: #f0f4f8;
}

.history_item.active {
  background-color: #eaf5f0;
  border-left: 3px solid #42b983;
}

.history_item_title {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.history_item_time {
  font-size: 12px;
  color: #909399;
}

.history_content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.history_content_header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.history_question {
  margin-left: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.history_content_body {
  flex-grow: 1;
  overflow-y: auto;
}

@media (min-width: 1200px) {
  .main_content {
    flex-direction: row;
  }
  
  .transcript_panel {
    width: 35%;
    max-height: none;
  }
  
  .answer_panel {
    width: 65%;
  }
}

/* 保留原有的样式... */
.ai_result_content.typing-effect {
  border-right: 2px solid #333;
  animation: blink-caret 0.75s step-end infinite;
}

@keyframes blink-caret {
  from, to { border-right-color: transparent; }
  50% { border-right-color: #333; }
}

/* 其他原有样式保持不变 */

/* Code blocks */
.code-block {
  background-color: #f8f8f8;
  border-radius: 6px;
  margin: 8px 0;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #42b983;
}

.code-header {
  background-color: #eaf5f0;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: bold;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
}

.code-content {
  margin: 0;
  padding: 12px;
  white-space: pre-wrap;
  font-family: 'Source Code Pro', 'Courier New', monospace;
  font-size: 14px;
  overflow-x: auto;
  color: #444;
  background-color: #f8f8f8;
}

/* Markdown specific styling */
:deep(.hljs) {
  background-color: #f8f8f8;
  padding: 0;
}

:deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
  margin-top: 16px;
  margin-bottom: 10px;
  font-weight: 600;
  line-height: 1.2;
  color: #24292e;
}

:deep(p) {
  margin-top: 0;
  margin-bottom: 8px;
  line-height: 1.5;
}

:deep(ul), :deep(ol) {
  padding-left: 2em;
  margin-top: 0;
  margin-bottom: 3px;
  padding-bottom: 0;
}

:deep(li) {
  margin: 0.1em 0;
  padding-bottom: 0;
}

:deep(blockquote) {
  padding: 0 1em;
  color: #6a737d;
  border-left: 0.25em solid #dfe2e5;
  margin: 0;
  margin-bottom: 10px;
}

:deep(pre) {
  padding: 8px;
  margin-bottom: 5px;
  overflow: auto;
  font-size: 85%;
  line-height: 1;
  background-color: #f6f8fa;
  border-radius: 3px;
}

:deep(code) {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(27,31,35,0.05);
  border-radius: 3px;
  font-family: "SFMono-Regular",Consolas,"Liberation Mono",Menlo,Courier,monospace;
}

:deep(table) {
  border-spacing: 0;
  border-collapse: collapse;
  margin-bottom: 10px;
  width: 100%;
}

:deep(table th),
:deep(table td) {
  padding: 6px 13px;
  border: 1px solid #dfe2e5;
}

:deep(table tr) {
  background-color: #fff;
  border-top: 1px solid #c6cbd1;
}

:deep(table tr:nth-child(2n)) {
  background-color: #f6f8fa;
}
</style>
